import { useState } from "react";
import { X, Search } from "lucide-react";
import { LUCIDE_ICON_ENTRIES, POPULAR_ICON_KEYS } from "./feedConstants";
import ColorWheelPicker from "./ColorWheelPicker";
import { createFeedView, updateFeedView } from "../../../api/feed";

const DEFAULT_FORM = {
  name: "",
  icon: "sliders-horizontal",
  color: "rgba(14,165,233,0.85)",
  description: "",
};

const CreateEditViewModal = ({ dark, t, editingView, onClose, onSaved }) => {
  const [form, setForm] = useState(() => {
    if (editingView) {
      return {
        name: editingView.name || "",
        icon: editingView.icon || "sliders-horizontal",
        color: editingView.color || "rgba(14,165,233,0.85)",
        description: editingView.description || "",
      };
    }
    return { ...DEFAULT_FORM };
  });

  const [modalIconSearch, setModalIconSearch] = useState("");

  /* ── Submit ── */
  const handleSubmit = async () => {
    try {
      const payload = {
        name: form.name,
        icon: form.icon,
        color: form.color,
        description: form.description,
      };

      let res;
      if (editingView) {
        payload.document_id = editingView.document_id;
        res = await updateFeedView(payload);
      } else {
        res = await createFeedView(payload);
      }

      if (res.data?.status === 200) {
        onSaved(res.data.view);
      } else if (res.data?.status === 409) {
        alert("A view with that name already exists.");
      }
    } catch (err) {
      console.error(
        editingView ? "Update view failed:" : "Create view failed:",
        err,
      );
    }
  };

  return (
    <div
      className="fixed inset-0 z-[70] flex items-center justify-center bg-black/40"
      onClick={onClose}
    >
      <div
        className="w-[580px] rounded-xl p-0 max-h-[85vh] flex flex-col"
        style={{
          background: dark ? "rgba(11,15,20,0.97)" : "rgba(255,255,255,0.98)",
          border: `1px solid ${t.barBorder}`,
          boxShadow: t.barShadow,
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 pt-4 pb-2 shrink-0">
          <h3
            className="text-sm font-medium tracking-wide"
            style={{ color: t.textPrimary }}
          >
            {editingView ? "Edit Feed View" : "Create Feed View"}
          </h3>
          <button
            className="p-1 rounded transition-colors"
            style={{ color: t.textMuted }}
            onClick={onClose}
          >
            <X size={14} />
          </button>
        </div>

        <div
          className="px-5 pb-4 overflow-y-auto flex-1"
          style={{ scrollbarWidth: "thin" }}
        >
          {/* Name */}
          <div className="mb-3">
            <label
              className="block text-xs mb-1.5"
              style={{ color: t.textSecondary }}
            >
              View Name
            </label>
            <input
              type="text"
              autoFocus
              className="w-full px-3 py-2 rounded text-sm outline-none transition-colors"
              placeholder="e.g. My APT Tracker"
              style={{
                background: t.inputBg,
                border: `1px solid ${t.inputBorder}`,
                color: t.inputText,
              }}
              value={form.name}
              onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
            />
          </div>

          {/* Icon & Color row */}
          <div className="grid grid-cols-2 gap-3 mb-3">
            {/* Icon picker — searchable */}
            <div>
              <label
                className="block text-xs mb-1.5"
                style={{ color: t.textSecondary }}
              >
                Icon
              </label>
              <div className="relative mb-1">
                <Search
                  size={11}
                  style={{
                    position: "absolute",
                    left: 8,
                    top: "50%",
                    transform: "translateY(-50%)",
                    opacity: 0.4,
                  }}
                />
                <input
                  type="text"
                  className="w-full pl-7 pr-3 py-1 rounded text-[11px] outline-none"
                  placeholder="Search icons..."
                  style={{
                    background: t.inputBg,
                    border: `1px solid ${t.inputBorder}`,
                    color: t.inputText,
                  }}
                  value={modalIconSearch}
                  onChange={(e) => setModalIconSearch(e.target.value)}
                />
              </div>
              <div
                className="flex flex-wrap gap-0.5 max-h-[200px] overflow-y-auto p-1.5 rounded"
                style={{
                  background: dark ? "rgba(17,24,39,0.5)" : "rgba(0,0,0,0.02)",
                  border: `1px solid ${dark ? "rgba(14,165,233,0.15)" : "rgba(0,0,0,0.06)"}`,
                  scrollbarWidth: "thin",
                }}
              >
                {(() => {
                  const search = modalIconSearch.toLowerCase().trim();
                  let filtered;
                  if (search) {
                    filtered = LUCIDE_ICON_ENTRIES.filter(
                      (e) =>
                        e.key.includes(search) ||
                        e.name.toLowerCase().includes(search),
                    );
                  } else {
                    filtered = LUCIDE_ICON_ENTRIES.filter((e) =>
                      POPULAR_ICON_KEYS.has(e.key),
                    );
                  }
                  // Always include the currently selected icon at the front
                  const currentKey = form.icon;
                  if (
                    currentKey &&
                    !filtered.some((e) => e.key === currentKey)
                  ) {
                    const current = LUCIDE_ICON_ENTRIES.find(
                      (e) => e.key === currentKey,
                    );
                    if (current) filtered = [current, ...filtered];
                  }
                  return filtered.map((entry) => {
                    const IconComp = entry.component;
                    const isActive = form.icon === entry.key;
                    return (
                      <button
                        key={entry.key}
                        type="button"
                        className="p-1.5 rounded transition-all"
                        title={entry.name}
                        style={{
                          background: isActive
                            ? "rgba(14,165,233,0.15)"
                            : "transparent",
                          border: isActive
                            ? "1px solid rgba(14,165,233,0.30)"
                            : "1px solid transparent",
                        }}
                        onClick={() =>
                          setForm((f) => ({
                            ...f,
                            icon: entry.key,
                          }))
                        }
                      >
                        <IconComp
                          size={14}
                          style={{
                            color: isActive
                              ? form.color
                              : dark
                                ? "rgba(148,163,184,0.5)"
                                : "rgba(100,116,139,0.5)",
                          }}
                        />
                      </button>
                    );
                  });
                })()}
              </div>
            </div>

            {/* Color picker */}
            <div>
              <label
                className="block text-xs mb-1.5"
                style={{ color: t.textSecondary }}
              >
                Color
              </label>
              <ColorWheelPicker
                value={form.color}
                onChange={(c) => setForm((f) => ({ ...f, color: c }))}
                dark={dark}
              />
            </div>
          </div>

          {/* Description */}
          <div className="mb-3">
            <label
              className="block text-xs mb-1.5"
              style={{ color: t.textSecondary }}
            >
              Description
            </label>
            <textarea
              className="w-full px-3 py-2 rounded text-sm outline-none transition-colors resize-none"
              rows={3}
              placeholder="Describe what you want to see, e.g. 'APT groups targeting healthcare and financial sectors' or 'ransomware attacks on critical infrastructure'"
              style={{
                background: t.inputBg,
                border: `1px solid ${t.inputBorder}`,
                color: t.inputText,
              }}
              value={form.description}
              onChange={(e) =>
                setForm((f) => ({ ...f, description: e.target.value }))
              }
            />
            <p className="mt-1 text-[10px]" style={{ color: t.textMuted }}>
              This description will be used to semantically match relevant feed
              items.
            </p>
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-2">
            <button
              className="px-4 py-1.5 rounded text-xs transition-colors"
              style={{
                color: t.textSecondary,
                border: `1px solid ${t.inputBorder}`,
              }}
              onClick={onClose}
            >
              Cancel
            </button>
            <button
              className="px-4 py-1.5 rounded text-xs text-white bg-sky-500 hover:bg-sky-600 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              disabled={!form.name.trim() || !form.description.trim()}
              onClick={handleSubmit}
            >
              {editingView ? "Save Changes" : "Create View"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreateEditViewModal;
